import Table from '@/components/Table';
import { TableData } from '@/components/Table/type';
import { useProductQuery } from '@/queries/useProductQuery';
import { Product } from '@/types/Product';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import DadosMestreHeader from '../components/DadosMestreHeader';
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions';
import DefaultColumn from '@/components/Table/components/DefaultColumn';
import { useTable } from '@/hooks/useTable';
import { useProductSpreadsheets } from './useProductSpreadsheets';
import TableRowActions from '@/components/Table/components/TableRowActions';
import AddDialog from './components/AddDialog';
import DeleteDialog from './components/DeleteDialog';
import EditDialog from './components/EditDialog';
import { Cog } from 'lucide-react';
import { TypographyH1, TypographyMuted } from '@/components/ui/typography';
import { useBaseStore } from '@/store';
import { authStateSelector } from '@/store/authSlice';
import { decodeJwt } from 'jose';

export type ProductTable = TableData<Product>;

const columnHelper = createColumnHelper<Product>();

const MasterDataProduct = () => {
  const {
    table,
    tableState,
    selectedRows,
    isAddDialogOpen,
    isDeleteDialogOpen,
    selectedRow,
    isEditDialogOpen,
    onEditDialogClose,
    onAddDialogClose,
    onClickMultiDelete,
    setTable,
    onClickDelete,
    onClickEdit,
    onDeleteDialogClose,
    onClickAdd,
  } = useTable<Product>('');

  const { onDownload } = useProductSpreadsheets();
  const { data, isFetching, isPending, refetch } = useProductQuery();

  const { token, profile } = useBaseStore(authStateSelector);
  const decodedToken = useMemo(() => {
    if (!token) return null;
    try {
      return decodeJwt(token) as any;
    } catch {
      return null;
    }
  }, [token]);

  const perfil = decodedToken?.usuario?.perfil;
  const revendasPermitidas: number[] = useMemo(() => {
    const list = decodedToken?.usuario?.revendas_permitidas || [];
    return list.map(Number);
  }, [decodedToken]);
  const memoData = useMemo(() => {
    if (data) {
      return data.map((product) => ({
        ...product,
        DD_ORIGEM_DADO:
          profile === 'ADMINISTRADOR_REVENDA'
            ? product.DD_ORIGEM_DADO === 'MANUAL'
              ? 'CENTRAL'
              : product.DD_ORIGEM_DADO
            : product.DD_ORIGEM_DADO,
      }));
    } else {
      return [];
    }
  }, [data]);
  const memoColumns = useMemo(
    () => [
      columnHelper.accessor('SK_PRODUTO', {
        id: 'SK_PRODUTO',
        header: 'Cod. Produto',
        size: 130,
      }),
      columnHelper.accessor('DS_PRODUTO', {
        id: 'DS_PRODUTO',
        header: 'Produto',
        size: 440,
      }),
      columnHelper.accessor('DD_SKU', {
        id: 'DD_SKU',
        header: 'Embalagem',
        size: 100,
      }),
      columnHelper.accessor('DD_UNIDADE_VENDA', {
        id: 'DD_UNIDADE_MEDIDA',
        header: 'Uni. Venda',
        size: 120,
      }),
      columnHelper.accessor('DD_MARCA', {
        id: 'DD_MARCA',
        header: 'Marca',
        size: 180,
      }),
      columnHelper.accessor('DS_DETENTOR', {
        id: 'DS_DETENTOR',
        header: 'Fabricante',
        size: 120,
      }),
      columnHelper.accessor('DD_ORIGEM_DADO', {
        id: 'DD_ORIGEM_DADO',
        header: 'Origem / Cadastro',
        cell: ({ row }) => {
          const orig = row.original.DD_ORIGEM_DADO;
          const resaleName = row.original.DS_REVENDA;
          if (
            orig === 'CENTRAL' ||
            orig === 'PROTHEUS' ||
            !row.original.SK_REVENDA
          ) {
            return (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Corporativo
              </span>
            );
          }
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
              {resaleName ? `Revenda (${resaleName})` : 'Revenda'}
            </span>
          );
        },
        size: 180,
      }),
      columnHelper.display({
        id: 'Ações',
        cell: ({ row }) => {
          if (row.original.DD_ORIGEM_DADO === 'PROTHEUS') return;
          if (perfil === 'ADMINISTRADOR_REVENDA') {
            if (
              row.original.DD_ORIGEM_DADO === 'CENTRAL' ||
              !row.original.SK_REVENDA
            )
              return;
            if (
              row.original.SK_REVENDA &&
              !revendasPermitidas.includes(Number(row.original.SK_REVENDA))
            )
              return;
          }
          return (
            <TableRowActions
              onClickDelete={() => onClickDelete(row)}
              onClickEdit={() => onClickEdit(row)}
            />
          );
        },
        size: 60,
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [perfil, revendasPermitidas],
  );

  return (
    <div className="flex flex-col h-full overflow-auto p-4">
      <div className="flex flex-col gap-4 justify-between mb-4 sm:items-center sm:flex-row">
        <div className="flex gap-3 items-center">
          <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
            <Cog className="w-5 h-5 text-primary" />
          </div>
          <div>
            <TypographyH1>Produtos</TypographyH1>
            <TypographyMuted>
              Cadastre e efetue manutenções em produtos
            </TypographyMuted>
          </div>
        </div>
      </div>

      <div className="w-full h-full overflow-hidden">
        <Table<ProductTable>
          data={memoData}
          columns={memoColumns}
          getRowId={(row) => row.SK_PRODUTO.toString()}
          isLoading={isPending}
          isFetching={isFetching}
          getTableInstance={(table) => setTable(table)}
          tableState={tableState}
          tableHeader={
            <DadosMestreHeader
              title={`${(data || []).length} itens`}
              onDelete={onClickMultiDelete}
              selectedRows={
                table
                  ?.getFilteredRowModel()
                  .flatRows.filter((row) => selectedRows[row.id]).length
              }
            />
          }
          tableActions={
            <DadosMestreHeaderActions
              onDownload={() => table && onDownload(table)}
              onRefech={refetch}
              onAdd={onClickAdd}
            />
          }
          defaultColumn={{
            cell: ({ getValue }) => (
              <DefaultColumn>{getValue() as string}</DefaultColumn>
            ),
          }}
        />

        {isDeleteDialogOpen && selectedRow && (
          <DeleteDialog
            ids={selectedRow.original.SK_PRODUTO}
            nameUser={selectedRow.original.DS_PRODUTO}
            isOpen={isDeleteDialogOpen}
            onClose={onDeleteDialogClose}
          />
        )}
        {selectedRow && (
          <EditDialog
            row={selectedRow}
            isOpen={isEditDialogOpen}
            onClose={onEditDialogClose}
          />
        )}
        <AddDialog
          isOpen={isAddDialogOpen}
          onClose={onAddDialogClose}
          isFixedMark={false}
        />
      </div>
    </div>
  );
};

export default MasterDataProduct;
